import { createSlice } from '@reduxjs/toolkit';
import { ReducerStatus } from 'utils/constants';
import {
  addClassUsersAction,
  createClassAction,
  deleteClassAction,
  deleteClassUserAction,
  getClassAction,
  getClassProgressionsAction,
  getClassRecordsAction,
  getClassesAction,
  updateClassAction,
} from './class-action';
import updatePublishStatusAction from '../common/publishEntity/publishEntity-action';

const classSlice = createSlice({
  name: 'class',
  initialState: {
    classes: [],
    class: null,
    classesMetaData: null,
    progressions: null,
    records: {
      users: [],
      courses: [],
      courseQuests: [],
      questSubQuests: [],
      progressionRecords: [],
    },
    getClasses: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    getClass: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    getClassProgressions: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    getClassRecords: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    createClass: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    addClassUsers: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    updateClass: {
      modal: false,
      id: null,
      status: ReducerStatus.IDLE,
      error: null,
    },
    deleteClass: {
      modal: false,
      id: null,
      status: ReducerStatus.IDLE,
      error: null,
    },
    deleteClassUser: {
      status: ReducerStatus.IDLE,
      error: null,
    },
  },
  reducers: {
    setCreateClass(state, action) {
      state.createClass = {
        ...state.createClass,
        ...action.payload,
      };
    },
    setAddClassUsers(state, action) {
      state.addClassUsers = {
        ...state.addClassUsers,
        ...action.payload,
      };
    },
    setUpdateClass(state, action) {
      state.updateClass = {
        ...state.updateClass,
        ...action.payload,
      };
    },
    setDeleteClass(state, action) {
      state.deleteClass = {
        ...state.deleteClass,
        ...action.payload,
      };
    },
    setDeleteClassUser(state, action) {
      state.deleteClassUser = {
        ...state.deleteClassUser,
        ...action.payload,
      };
    },
    removeProgressionData(state, action) {
      const filteredProgressionRecords =
        state.records.progressionRecords.filter(
          (item) => item.id !== action.payload
        );

      state.records.progressionRecords = filteredProgressionRecords;
    },
  },
  extraReducers(builder) {
    builder
      // Get Classes
      .addCase(getClassesAction.pending, (state) => {
        state.getClasses.status = ReducerStatus.LOADING;
      })
      .addCase(getClassesAction.fulfilled, (state, action) => {
        state.getClasses.status = ReducerStatus.SUCCEEDED;
        const { metaData, data } = action.payload;

        state.classes = data;
        state.classesMetaData = metaData;
      })
      .addCase(getClassesAction.rejected, (state, action) => {
        state.getClasses.status = ReducerStatus.FAILED;
        state.getClasses.error = action.payload;
      })
      // Get Class
      .addCase(getClassAction.pending, (state) => {
        state.getClass.status = ReducerStatus.LOADING;
      })
      .addCase(getClassAction.fulfilled, (state, action) => {
        state.getClass.status = ReducerStatus.SUCCEEDED;
        state.class = action.payload;
      })
      .addCase(getClassAction.rejected, (state, action) => {
        state.getClass.status = ReducerStatus.FAILED;
        state.getClass.error = action.payload;
      })
      // Get Class Progressions
      .addCase(getClassProgressionsAction.pending, (state) => {
        state.getClassProgressions.status = ReducerStatus.LOADING;
      })
      .addCase(getClassProgressionsAction.fulfilled, (state, action) => {
        state.getClassProgressions.status = ReducerStatus.SUCCEEDED;
        state.progressions = action.payload;
      })
      .addCase(getClassProgressionsAction.rejected, (state, action) => {
        state.getClassProgressions.status = ReducerStatus.FAILED;
        state.getClassProgressions.error = action.payload;
      })
      // Get Class Records
      .addCase(getClassRecordsAction.pending, (state) => {
        state.getClassRecords.status = ReducerStatus.LOADING;
      })
      .addCase(getClassRecordsAction.fulfilled, (state, action) => {
        state.getClassRecords.status = ReducerStatus.SUCCEEDED;
        state.records = action.payload;
      })
      .addCase(getClassRecordsAction.rejected, (state, action) => {
        state.getClassRecords.status = ReducerStatus.FAILED;
        state.getClassRecords.error = action.payload;
      })
      // Create Class
      .addCase(createClassAction.pending, (state) => {
        state.createClass.status = ReducerStatus.LOADING;
      })
      .addCase(createClassAction.fulfilled, (state, action) => {
        state.createClass.status = ReducerStatus.SUCCEEDED;
        state.classes = [action.payload, ...state.classes];
        state.class = action.payload;
      })
      .addCase(createClassAction.rejected, (state, action) => {
        state.createClass.status = ReducerStatus.FAILED;
        state.createClass.error = action.payload;
      })
      // Add Class Users
      .addCase(addClassUsersAction.pending, (state) => {
        state.addClassUsers.status = ReducerStatus.LOADING;
      })
      .addCase(addClassUsersAction.fulfilled, (state, action) => {
        state.addClassUsers.status = ReducerStatus.SUCCEEDED;
        state.records.users = action.payload.users;
      })
      .addCase(addClassUsersAction.rejected, (state, action) => {
        state.addClassUsers.status = ReducerStatus.FAILED;
        state.addClassUsers.error = action.payload;
      })
      // Update Class
      .addCase(updateClassAction.pending, (state) => {
        state.updateClass.status = ReducerStatus.LOADING;
      })
      .addCase(updateClassAction.fulfilled, (state, action) => {
        state.updateClass.status = ReducerStatus.SUCCEEDED;
        const updateClasses = state.classes.map((classItem) =>
          classItem.id === action.payload.id ? action.payload : classItem
        );
        state.classes = updateClasses;
        state.class = action.payload;
      })
      .addCase(updateClassAction.rejected, (state, action) => {
        state.updateClass.status = ReducerStatus.FAILED;
        state.updateClass.error = action.payload;
      })
      // Delete Class
      .addCase(deleteClassAction.pending, (state) => {
        state.deleteClass.status = ReducerStatus.LOADING;
      })
      .addCase(deleteClassAction.fulfilled, (state, action) => {
        state.deleteClass.status = ReducerStatus.SUCCEEDED;
        const filteredClasses = state.classes.filter(
          (classItem) => classItem.id !== action.payload
        );
        state.classes = filteredClasses;
      })
      .addCase(deleteClassAction.rejected, (state, action) => {
        state.deleteClass.status = ReducerStatus.FAILED;
        state.deleteClass.error = action.payload;
      })
      // Update Publish Status
      .addCase(updatePublishStatusAction.fulfilled, (state, action) => {
        const { entityType, entityId, isPublished } = action.payload;

        if (entityType === 'Class') {
          if (state.class && state.class.id === entityId) {
            state.class.isPublished = isPublished;
          }
          state.classes = state.classes.map((classItem) =>
            classItem.id === entityId
              ? { ...classItem, isPublished }
              : classItem
          );
        }
      })
      // Delete Class User
      .addCase(deleteClassUserAction.pending, (state) => {
        state.deleteClassUser.status = ReducerStatus.LOADING;
      })
      .addCase(deleteClassUserAction.fulfilled, (state, action) => {
        state.deleteClassUser.status = ReducerStatus.SUCCEEDED;
        const filteredUsers = state.records.users.filter(
          (user) => user.id !== action.payload
        );
        state.records.users = filteredUsers;
      })
      .addCase(deleteClassUserAction.rejected, (state, action) => {
        state.deleteClassUser.status = ReducerStatus.FAILED;
        state.deleteClassUser.error = action.payload;
      });
  },
});

export const classActions = classSlice.actions;

export default classSlice;
